import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import { colors, fonts } from 'bench-style-constants';

import Button from './Button';
import CalendarYear from './CalendarYear';
import Triangle from './Triangle';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const StartWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  padding: 0 16px 16px;
  align-items: center;
`;

const CheckLabel = styled.label`
  font-size: 14px;
  color: ${colors.smoke};
`;

const CheckInput = styled.input`
  margin-right: 8px;
`;

const CalendarYearWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 240px;
  font-family: ${fonts.circular};
  font-size: 14px;
  padding: 8px 12px;
`;

const Year = styled.div`
  font-weight: bold;
  font-size: 14px;
  text-align: center;
  padding: 8px 12px;
`;

const CalendarTitleLayout = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr 20px;
  align-items: center;
  text-align: center;
  width: 100%;
`;

export const ArrowButton = styled.button`
  border: 0;
  background: transparent;
  padding: 8px;

  &:hover {
    box-shadow: none;
  }
`;

export type CalendarYearsProps = {
  onSubmit: (restoreLastChecks: boolean, startDate: string, endDate: string) => void;
};

export type CalendarYearsState = {
  currentYear: number;
  yearMonthHover: string | null;
  startDate: string | null;
  endDate: string | null;
  restoreLastChecks: boolean;
};

class CalendarYears extends Component<CalendarYearsProps, CalendarYearsState> {
  constructor(props: CalendarYearsProps) {
    super(props);

    this.state = {
      currentYear: new Date().getFullYear(),
      yearMonthHover: null,
      startDate: null,
      endDate: null,
      restoreLastChecks: false,
    };
  }

  onDateSelect = (yearMonth: string) => {
    const { startDate, endDate } = this.state;

    if (startDate && !endDate) {
      // When selecting an endDate before the startDate, just set it as a new startDate.
      if (yearMonth < startDate) {
        return this.setState({
          startDate: yearMonth,
        });
      }

      return this.setState({
        endDate: yearMonth,
      });
    }

    return this.setState({
      startDate: yearMonth,
      endDate: null,
    });
  };

  onYearChange(year: number) {
    this.setState({ currentYear: year });
  }

  onYearIncrease = () => {
    this.onYearChange(this.state.currentYear + 1);
  };

  onYearDecrease = () => {
    this.onYearChange(this.state.currentYear - 1);
  };

  onMonthHover = (yearMonth: string) => {
    this.setState({ yearMonthHover: yearMonth });
  };

  onMonthUnhover = (yearMonth: string) => {
    this.setState(prevState => {
      if (prevState.yearMonthHover === yearMonth) {
        return { yearMonthHover: null };
      }

      return null;
    });
  };

  onSubmit = () => {
    const { restoreLastChecks, startDate, endDate } = this.state;

    if (startDate) {
      if (endDate) {
        this.props.onSubmit(restoreLastChecks, startDate, endDate);
      } else {
        this.props.onSubmit(restoreLastChecks, startDate, startDate);
      }
    }
  };

  toggleRestoreLastChecks = () => {
    this.setState({ restoreLastChecks: !this.state.restoreLastChecks });
  };

  render() {
    const { currentYear, yearMonthHover, startDate, endDate, restoreLastChecks } = this.state;

    return (
      <Fragment>
        <Wrapper>
          <CalendarYearWrapper>
            <CalendarTitleLayout>
              <ArrowButton onClick={this.onYearDecrease}>
                <Triangle left size="5px" data-test-id="year-previous" />
              </ArrowButton>
              <Year>{currentYear - 1}</Year>
              <div />
              {/* used as a spacer for the grid layout */}
            </CalendarTitleLayout>
            <CalendarYear
              year={currentYear - 1}
              selectedStartDate={startDate}
              selectedEndDate={endDate}
              yearMonthHover={yearMonthHover}
              onDateSelect={this.onDateSelect}
              onHover={this.onMonthHover}
              onUnhover={this.onMonthUnhover}
            />
          </CalendarYearWrapper>
          <CalendarYearWrapper>
            <CalendarTitleLayout>
              <div />
              {/* used as a spacer for the grid layout */}
              <Year>{currentYear}</Year>
              <ArrowButton onClick={this.onYearIncrease}>
                <Triangle right size="5px" data-test-id="year-next" />
              </ArrowButton>
            </CalendarTitleLayout>
            <CalendarYear
              year={currentYear}
              selectedStartDate={startDate}
              selectedEndDate={endDate}
              onDateSelect={this.onDateSelect}
              yearMonthHover={yearMonthHover}
              onHover={this.onMonthHover}
              onUnhover={this.onMonthUnhover}
            />
          </CalendarYearWrapper>
        </Wrapper>
        <StartWrapper>
          <CheckLabel htmlFor="restoreLastChecks">
            <CheckInput
              type="checkbox"
              id="restoreLastChecks"
              checked={restoreLastChecks}
              onChange={this.toggleRestoreLastChecks}
            />
            Show last recorded statuses
          </CheckLabel>
          <Button
            data-heap-id="review-tool-month-range-picker-start"
            data-test-id="date-range-submit"
            disabled={!startDate}
            onClick={this.onSubmit}
          >
            Start
          </Button>
        </StartWrapper>
      </Fragment>
    );
  }
}

export default CalendarYears;
